/* hs-eslint ignored failing-rules */

/* eslint-disable @typescript-eslint/no-floating-promises */
import { useEffect, useMemo } from 'react';
import { useUserAttribute, UserAttributeKeys } from '../userAttributes';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import { wizardEnabledLanguages } from '../../constants/WizardRecommendationConstants';
import getLang from 'I18n/utils/getLang';
import { Set as ImmutableSet } from 'immutable';
import { HUBSPOT_SOFTWARE_INDEX } from './wizardTypes';
import { mapWizardCategories } from '../../constants/TopFilterConstants';
import { useFilters } from '../filters';
import { useWizardParams } from '../../hooks/useWizard';
export const useShowWizardLanguageToggle = () => {
  const language = getLang();
  return {
    languageName: LanguageNames[language],
    show: language !== 'en' && wizardEnabledLanguages.includes(language)
  };
};
export const useSavedLearningInterest = () => {
  const {
    userAttributeLoading: isLoadingLearningInterest,
    userAttribute: wizardResults,
    userAttributesError: learningInterestError
  } = useUserAttribute(UserAttributeKeys.WIZARD_RESULTS);
  const savedState = {
    resultCount: 0,
    isToolsSelected: false,
    showOnlyCerts: false,
    isLoadingLearningInterest,
    hasLearningInterest: !!wizardResults,
    learningInterestError
  };

  if (wizardResults) {
    return Object.assign({}, savedState, {
      selectedTool: wizardResults.selectedTool,
      isToolsSelected: wizardResults.isToolsSelected,
      selectedToolsGroup: wizardResults.selectedToolsGroup,
      selectedCategoryGroup: wizardResults.selectedCatGroup,
      selectedCategory: wizardResults.selectedCat,
      showOnlyCerts: wizardResults.selectedShowCertificatesOnly,
      resultCount: wizardResults.resultCount
    });
  }

  return savedState;
};
export const useCurrentLearningInterest = () => {
  const {
    isToolsSelected,
    categoryGroup,
    category,
    showOnlyCerts
  } = useWizardParams();
  return useMemo(() => {
    return {
      showOnlyCerts: showOnlyCerts === true,
      isToolsSelected,
      selectedToolsGroup: isToolsSelected ? categoryGroup : undefined,
      selectedTool: isToolsSelected ? category : undefined,
      selectedCategoryGroup: !isToolsSelected ? categoryGroup : undefined,
      selectedCategory: !isToolsSelected ? category : undefined
    };
  }, [showOnlyCerts, isToolsSelected, categoryGroup, category]);
};
export const useWizardCategoriesMap = () => {
  const {
    filters,
    filtersLoading
  } = useFilters();
  const wizardCategoryMap = useMemo(() => {
    if (!filters) return null;
    const {
      hsSoftwareFilters,
      subCategories
    } = filters;
    return mapWizardCategories(ImmutableSet(subCategories), ImmutableSet(hsSoftwareFilters)).toJS();
  }, [filters]);
  return {
    wizardCategoryMap,
    isLoading: filtersLoading
  };
};

const getStepDepth = value => {
  if (Array.isArray(value)) {
    if (value.length) return 1 + Math.max(0, ...value.map(getStepDepth));
  } else if (typeof value === 'object') {
    return 1 + Math.max(0, ...Object.values(value).map(getStepDepth));
  }

  return 0;
};

export const useWizardProgress = () => {
  const {
    isToolsSelected,
    categoryGroup,
    category,
    showOnlyCerts
  } = useWizardParams();
  const {
    wizardCategoryMap,
    isLoading
  } = useWizardCategoriesMap();
  let currentStep = 'initial';
  let wizardProgress = 3;

  if (showOnlyCerts !== undefined) {
    currentStep = 'final';
    wizardProgress = 100;
  } else if (!isLoading && (isToolsSelected || categoryGroup)) {
    let currentStepMap = wizardCategoryMap;
    let currentStepDepth = 0;

    if (isToolsSelected) {
      currentStepDepth++;
      currentStepMap = currentStepMap[HUBSPOT_SOFTWARE_INDEX];
    }

    if (categoryGroup) {
      currentStep = 'category';
      currentStepDepth++;
      currentStepMap = currentStepMap[categoryGroup];

      if (category) {
        currentStepDepth++;
        currentStepMap = currentStepMap[category];
      }
    }

    const depth = getStepDepth(currentStepMap);
    if (!depth) currentStep = 'motivation';
    wizardProgress = Math.round(currentStepDepth * 100 / (depth + currentStepDepth + 1));
  }

  return {
    currentStep,
    wizardProgress,
    isLoading
  };
};
export const useSubmitWizardResults = ({
  resultCount,
  wait
}) => {
  const {
    isToolsSelected,
    selectedCategoryGroup,
    selectedCategory,
    selectedToolsGroup,
    selectedTool,
    showOnlyCerts
  } = useCurrentLearningInterest();
  const {
    currentStep
  } = useWizardProgress();
  const {
    setUserAttribute: setWizardResults,
    userAttributeLoading
  } = useUserAttribute(UserAttributeKeys.WIZARD_RESULTS);
  useEffect(() => {
    if (!wait && !userAttributeLoading && currentStep === 'final') {
      setWizardResults({
        isToolsSelected,
        selectedCatGroup: isToolsSelected ? '' : selectedCategoryGroup,
        selectedCat: isToolsSelected ? '' : selectedCategory,
        selectedToolsGroup: isToolsSelected ? selectedToolsGroup : '',
        selectedTool: isToolsSelected ? selectedTool : '',
        selectedShowCertificatesOnly: showOnlyCerts,
        resultCount
      });
    }
  }, [setWizardResults, resultCount, isToolsSelected, selectedCategoryGroup, selectedCategory, selectedToolsGroup, selectedTool, showOnlyCerts, wait, userAttributeLoading, currentStep]);
};